import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Box,
  Paper,
} from '@mui/material';
import { CollaborationContractModal } from '@/features/collaborations/CollaborationContractModal.tsx';
import { useListCollaborations } from '@/lib/api/collaborations.ts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import { useGetMe } from '@/lib/api/me';
import { CollaborationDTO } from '@inversor-hub/types';
import PersonIcon from '@mui/icons-material/Person';
import UserDetailsSectionForm from '@/features/user/update-profile/UserDetailsSectionForm.tsx';
import {
  useGetSignature,
  useUpdateMe,
  useUploadProfilePhoto,
  useUploadSignature,
} from '@/lib/api/me.ts';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider.tsx';

interface InvestorContractModalProps {
  open: boolean;
  onClose: () => void;
  psiId: string;
  onConfirm: () => void;
}

function hasUserSigned(collab: CollaborationDTO | undefined, role: 'investor' | 'psi') {
  if (!collab) return false;
  return role === 'investor' ? collab.investorSigned : collab.psiSigned;
}

function bothHaveSigned(collab: CollaborationDTO | undefined) {
  if (!collab) return false;
  return collab.investorSigned && collab.psiSigned;
}

const ReserveOpportunityModal = ({
  open,
  onClose,
  psiId,
  onConfirm,
}: InvestorContractModalProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const { data: collaborations = [] } = useListCollaborations<'investor'>();

  const collaboration = collaborations.find((collab) => collab.psi?.id === psiId);

  const hasSignedCollaboration = collaboration && bothHaveSigned(collaboration);
  const hasUserSignedContract = collaboration && hasUserSigned(collaboration, 'investor');
  const { data: userData, refetch: refetchUserData } = useGetMe();
  const { mutateAsync: uploadSignature } = useUploadSignature();
  const { data: signatureDataUrl } = useGetSignature();
  const { mutateAsync: updateMe } = useUpdateMe();
  const { mutateAsync: uploadProfilePhoto } = useUploadProfilePhoto();

  const profileCompleted = userData?.profileCompleted ?? false;
  const totalSteps = profileCompleted ? 2 : 3;

  const handleNext = async () => {
    if (activeStep === 1 && !profileCompleted) {
      const updatedUser = await refetchUserData();
      if (!updatedUser.data?.profileCompleted) {
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };

  const getCurrentLogicalStep = () => {
    if (activeStep === 0) return 'info';
    if (!profileCompleted && activeStep === 1) return 'profile';
    return 'contract';
  };

  const renderStepContent = () => {
    const logicalStep = getCurrentLogicalStep();

    if (logicalStep === 'info') {
      return (
        <Box py={2}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default' }}>
            <Stack spacing={3}>
              <Box textAlign="center">
                <CheckCircleIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  ¡Has sido seleccionado!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  El PSI ha seleccionado tu perfil para esta oportunidad de inversión.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  ¿Qué significa esto?
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    • El PSI ha evaluado tu perfil y considera que eres un buen candidato
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Para reservar la oportunidad, necesitas firmar el contrato de colaboración
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Una vez firmado, la oportunidad quedará reservada exclusivamente para ti
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Próximos pasos:
                </Typography>
                <Stack spacing={1}>
                  {!profileCompleted && (
                    <Typography variant="body2" color="text.secondary">
                      1. Completar tus datos personales
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {profileCompleted ? '1' : '2'}. Revisar y firmar el contrato de colaboración
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profileCompleted ? '2' : '3'}. Confirmar la reserva de la oportunidad
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profileCompleted ? '3' : '4'}. Acceder a los detalles completos de la inversión
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Box>
      );
    }

    if (logicalStep === 'profile') {
      if (!userData) return null;
      return (
        <Box sx={{ pb: 0 }}>
          <Box textAlign="center" mb={3}>
            <PersonIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Completa tu perfil
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Para continuar con la reserva, necesitas completar tus datos personales.
            </Typography>
          </Box>
          <UserDetailsSectionForm
            currentUser={userData}
            signatureDataUrl={signatureDataUrl}
            uploadProfilePhoto={uploadProfilePhoto}
            uploadSignature={uploadSignature}
            updateMe={updateMe}
            onSuccess={handleNext}
          />
        </Box>
      );
    }

    if (logicalStep === 'contract') {
      return hasSignedCollaboration ? (
        <Box textAlign="center" py={4}>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            ¡Contrato ya firmado!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            El contrato de colaboración ya está firmado por ambas partes.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Al confirmar, se creará la reserva inmediatamente.
          </Typography>
        </Box>
      ) : (
        <Box py={2}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default' }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Firma del contrato de colaboración
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Para reservar esta oportunidad, necesitas firmar el contrato de colaboración con
                  el PSI.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  ¿Qué incluye el contrato?
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    • Términos y condiciones de la colaboración
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Responsabilidades de ambas partes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Proceso de inversión y gestión
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setContractModalOpen(true)}
                  startIcon={<DescriptionIcon />}
                >
                  Revisar y firmar contrato
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return null;
  };

  return (
    <GoogleMapsProvider>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll={getCurrentLogicalStep() === 'profile' ? 'body' : 'paper'}
      >
        <DialogTitle>
          <Stack spacing={1}>
            <Typography variant="h6">Reservar oportunidad</Typography>
            <Typography variant="body1" color="secondary">
              Has sido seleccionado para esta oportunidad. Completa el proceso para reservarla.
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            pb: getCurrentLogicalStep() === 'profile' ? 1 : 3,
            overflow: getCurrentLogicalStep() === 'profile' ? 'visible' : 'auto',
            maxHeight: getCurrentLogicalStep() === 'profile' ? 'none' : undefined,
          }}
        >
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
            <Step>
              <StepLabel>Información</StepLabel>
            </Step>
            {!profileCompleted && (
              <Step>
                <StepLabel>Completar perfil</StepLabel>
              </Step>
            )}
            <Step>
              <StepLabel>
                {hasSignedCollaboration ? 'Confirmar reserva' : 'Firmar contrato'}
              </StepLabel>
            </Step>
          </Stepper>
          {renderStepContent()}
        </DialogContent>
        <DialogActions>
          {activeStep === 0 ? (
            <>
              <Button onClick={handleClose}>Cancelar</Button>
              <Button onClick={handleNext} variant="contained" color="primary">
                Continuar
              </Button>
            </>
          ) : activeStep < totalSteps - 1 ? (
            <>
              <Button onClick={handleBack}>Atrás</Button>
              {getCurrentLogicalStep() !== 'profile' && (
                <Button onClick={handleNext} variant="contained" color="primary">
                  Continuar
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={handleBack}>Atrás</Button>
              {hasSignedCollaboration && (
                <Button onClick={handleConfirm} variant="contained" color="primary">
                  Confirmar reserva
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {collaboration && (
        <CollaborationContractModal
          open={contractModalOpen}
          onClose={() => setContractModalOpen(false)}
          collaborationId={collaboration.id}
          userRole="investor"
          hasUserSigned={hasUserSignedContract || false}
          hasUserSignature={Boolean(userData?.signature && userData.signature.trim())}
          bothHaveSigned={bothHaveSigned(collaboration) || false}
          onSignSuccess={() => {
            setContractModalOpen(false);
            handleConfirm();
          }}
        />
      )}
    </GoogleMapsProvider>
  );
};

export default ReserveOpportunityModal;
